import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const splitSchema = z.object({
  qtyToSplit: z.coerce.number().min(1, "Số lượng phải lớn hơn 0"),
  splitReason: z.string().optional(),
});

type SplitFormValues = z.infer<typeof splitSchema>;

interface SplitMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    id: string;
    qtyToSplit: number;
    splitReason?: string;
  }) => void;
  item: any | null;
}

export function SplitMaterialDialog({
  open,
  onOpenChange,
  onSubmit,
  item,
}: SplitMaterialDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SplitFormValues>({
    resolver: zodResolver(splitSchema),
    defaultValues: {
      qtyToSplit: 0,
      splitReason: "",
    },
  });

  const handleSubmit = async (values: SplitFormValues) => {
    if (!item) return;
    if (values.qtyToSplit >= (item.neededQty || item.requiredQty)) {
      form.setError("qtyToSplit", {
        type: "manual",
        message: "Số lượng tách phải nhỏ hơn số lượng yêu cầu của bản ghi gốc",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        id: item.id,
        qtyToSplit: values.qtyToSplit,
        splitReason: values.splitReason,
      });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Tách kế hoạch vật tư</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-500 mb-4">
          <p>
            Vật tư: <strong>{item.materialName}</strong>
          </p>
          <p>
            Số lượng yêu cầu hiện tại:{" "}
            <strong>{item.neededQty || item.requiredQty}</strong> {item.unit}
          </p>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="qtyToSplit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Số lượng cần tách</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="splitReason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lý do tách (tùy chọn)</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Ví dụ: Tách mua lẻ..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Xác nhận tách
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
